/**
 * locationFilterHelpers.ts
 * Shared utility for resolving Region/Market selections to Location IDs.
 * Used consistently across all endpoints.
 */

import type { LocationResolutionFilters } from '../types/filterTypes';

// ─── Helper Functions ───────────────────────────────────────────────────

const normalizeFilterValue = (value: unknown): string =>
  String(value ?? '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase();

const getRegionLabel = (loc: any): string =>
  String(loc?.region ?? loc?.unit ?? '').trim();

const getMarketLabel = (loc: any): string =>
  String(loc?.market ?? loc?.pod ?? loc?.marketPod ?? loc?.marketName ?? '').trim();

const getLocationLabel = (loc: any): string =>
  String(
    loc?.locName ?? loc?.locationName ?? loc?.name ?? loc?.locId ?? loc?.locationId ?? ''
  ).trim();

// ─── Main Export ────────────────────────────────────────────────────────

/**
 * Resolves selected Region/Market/Location filters to an array of Location IDs.
 * 
 * Priority:
 * 1. If explicit locations are selected → use them directly
 * 2. Otherwise → resolve from Region/Market hierarchy by matching against adminLocations
 * 
 * Empty selections are treated as "all" (no filter applied).
 * 
 * @param filters - User selections from dropdowns
 * @param adminLocations - Full list of available locations with hierarchy data
 * @returns Array of location IDs that match the filter criteria
 */
export function resolveLocationIdsFromFilters(
  filters: LocationResolutionFilters,
  adminLocations: any[]
): string[] {
  const selectedLocationIds = new Set<string>();

  // Priority 1: Explicit location selections
  if (filters.selectedLocations.length > 0) {
    filters.selectedLocations.forEach((loc) => {
      const locId = String(loc.id).trim();
      if (locId) selectedLocationIds.add(locId);
    });
    return Array.from(selectedLocationIds);
  }

  // Priority 2: Resolve from Region/Market hierarchy
  const selectedRegionIds = new Set(
    filters.selectedRegions.map((r) => normalizeFilterValue(r.id))
  );
  const selectedMarketIds = new Set(
    filters.selectedMarkets.map((m) => normalizeFilterValue(m.id))
  );

  // If neither region nor market filters are applied, return empty (means "all")
  if (selectedRegionIds.size === 0 && selectedMarketIds.size === 0) {
    return [];
  }

  // Match locations against selected regions/markets
  adminLocations.forEach((loc) => {
    const regionLabel = normalizeFilterValue(getRegionLabel(loc));
    const marketLabel = normalizeFilterValue(getMarketLabel(loc));
    const locId = String(loc.locId ?? getLocationLabel(loc) ?? '').trim();

    if (!locId) return;

    // Check if location matches both region and market criteria
    const regionMatches = selectedRegionIds.size === 0 || selectedRegionIds.has(regionLabel);
    const marketMatches = selectedMarketIds.size === 0 || selectedMarketIds.has(marketLabel);

    if (regionMatches && marketMatches) {
      selectedLocationIds.add(locId);
    }
  });

  return Array.from(selectedLocationIds);
}

/**
 * Normalizes a filter value by trimming and lowercasing for comparison.
 * Exported for testing purposes.
 */
export function normalizeValue(value: unknown): string {
  return normalizeFilterValue(value);
}
